import { useEffect, useState } from 'react';
import { Lock } from 'lucide-react';
import { apiClient } from '../../api';
import Drawer from '../UI/Drawer';
import { monoLabel, primaryBtn, primaryBtnStyle, ghostBtn, ghostBtnStyle, fieldLabel, fieldLabelStyle, inputStyle, errMessage } from '../UI/ink';

const Label = ({ children }) => <span className={monoLabel} style={{ color: 'var(--ink-faint)' }}>{children}</span>;

// Hands a draft to the builder: blank, or pre-filled from a template.
export const NewPlaybookDrawer = ({ open, templates, agentName, onClose, onStart }) => (
  <Drawer open={open} onClose={onClose} kicker="PLAYBOOKS" title="New playbook" subtitle="Start blank, or from a template you can change before saving.">
    <div className="flex flex-col gap-3">
      <button type="button" onClick={() => onStart({})} className="flex flex-col gap-1 px-4 py-3.5 rounded-xl border border-dashed text-left hover:border-[color:var(--accent)]" style={{ borderColor: 'var(--surf-line-strong)' }}>
        <span className="text-sm font-semibold">Blank playbook</span>
        <span className="text-[12.5px]" style={{ color: 'var(--ink-muted)' }}>Add phases and pick an agent for each.</span>
      </button>
      {templates.length > 0 && <div className="pt-2"><Label>TEMPLATES · {templates.length}</Label></div>}
      {templates.map(t => (
        <button
          key={t.id}
          type="button"
          onClick={() => onStart({
            name: t.name, description: t.description, use_case: t.description, trigger_examples: [],
            agents: t.agents || [], phases: t.phases || [],
          })}
          className="flex flex-col gap-2 px-4 py-3.5 rounded-xl border text-left hover:border-[color:var(--accent)]"
          style={{ borderColor: 'var(--surf-line)', background: 'var(--surf-bg)' }}
        >
          <span className="text-sm font-semibold">{t.name}</span>
          {t.description && <span className="text-[12.5px] leading-normal" style={{ color: 'var(--ink-muted)' }}>{t.description}</span>}
          <span className="flex flex-wrap items-center gap-[5px] font-mono text-[11px]" style={{ color: 'var(--ink-faint)' }}>
            {(t.phases || []).map((p, i) => (
              <span key={p.phase_id || i} className="inline-flex items-center gap-[5px]">
                {i > 0 && <span aria-hidden="true" style={{ color: 'var(--surf-line-strong)' }}>›</span>}
                {p.approval_required && <Lock className="w-[11px] h-[11px]" strokeWidth={2.2} aria-label="approval gate" />}
                {agentName(p.agent_id)}
              </span>
            ))}
          </span>
          {t.references?.length > 0 && (
            <span className="text-[11.5px] leading-normal" style={{ color: 'var(--ink-faint)' }}>
              Based on: {t.references.join(' · ')}
            </span>
          )}
        </button>
      ))}
    </div>
  </Drawer>
);

export const GenerateDrawer = ({ open, onClose, onGenerated }) => {
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { if (open) { setError(null); setBusy(false); } }, [open]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!description.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiClient.post('/api/workflows/ai-generate', { description: description.trim() });
      const g = res.data?.workflow || {};
      setDescription('');
      onGenerated({
        name: g.name || '', description: g.description || '', use_case: g.use_case || '',
        trigger_examples: g.trigger_examples || [], agents: g.agents || [], phases: g.phases || [],
      });
    } catch (err) {
      setError(errMessage(err, 'Generation failed. Try again, or start from a template.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      kicker="PLAYBOOKS"
      title="Generate with AI"
      subtitle="Describe the scenario in plain English. You’ll review the phases in the builder before anything is saved."
      footer={(
        <>
          <span />
          <div className="flex gap-2.5">
            <button type="button" onClick={onClose} className={ghostBtn} style={ghostBtnStyle}>Cancel</button>
            <button type="submit" form="ai-generate-form" disabled={busy || !description.trim()} className={primaryBtn} style={primaryBtnStyle}>{busy ? 'Generating…' : 'Generate'}</button>
          </div>
        </>
      )}
    >
      <form id="ai-generate-form" onSubmit={submit} className="flex flex-col gap-3">
        {error && <div role="alert" className="px-3.5 py-2.5 rounded-[10px] border text-[13px]" style={{ borderColor: 'var(--sev-crit-fg)', color: 'var(--sev-crit-fg)', background: 'var(--sev-crit-bg)' }}>{error}</div>}
        <label htmlFor="ai-desc" className={fieldLabel} style={fieldLabelStyle}>Scenario</label>
        <textarea
          id="ai-desc"
          rows={7}
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="e.g. Respond to a ransomware detection: triage the alert, work out the blast radius, isolate affected hosts after approval, and write the incident report."
          className="box-border px-3 py-2.5 rounded-[10px] border text-sm leading-normal resize-none outline-none focus:border-[color:var(--accent)]"
          style={inputStyle}
        />
      </form>
    </Drawer>
  );
};
